<template>
  <div class="dialog-step">
    <div v-if="tokenPreview" class="token-info">
      <TokenIcon
        :logo="tokenPreview?.logo"
        :type="tokenPreview?.type"
        size="64"
        class="img"
      />
      <div class="token-info-content">
        <span>{{ tokenPreview.symbol }}</span>
        <span>{{ tokenPreview.name }}</span>
      </div>
      <span class="token-info-address">{{ tokenPreview.address }} </span>
    </div>
    <div v-else class="warning-container">
      <img src="@/assets/warning.svg" alt="" />
      <div class="warning-container-content">
        <span>{{ $t('dialogs.tokenNotFound') }}</span>
        <span>{{ $t('dialogs.tokenNotFoundDescription') }}</span>
      </div>
    </div>
    <div v-if="tokenPreview" class="warning-container">
      <img src="@/assets/warning.svg" alt="" />
      <div class="warning-container-content">
        <span>{{ $t('dialogs.importOnYourRisk') }}</span>
        <span v-html="$t('dialogs.importOnYourRiskDescription')" />
      </div>
    </div>
    <div v-if="tokenPreview" class="dialog-step__footer">
      <div class="footer-buttons">
        <PButton
          class="p-button-secondary w-full relative flex footer-button justify-content-center circular-button"
          @click="onClose"
        >
          {{ $t('common.cancel') }}
        </PButton>
        <PButton
          class="w-full relative flex align-items-center justify-content-center p-button-primary"
          @click="importToken"
        >
          <span class="font-medium">{{ $t('common.import') }}</span>
        </PButton>
      </div>
    </div>
    <div v-else class="dialog-step__footer">
      <PButton
        class="p-button-primary w-full relative flex align-items-center justify-content-center"
        @click="onClose"
      >
        <span class="font-medium">{{ $t('common.ok') }}</span>
      </PButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, onBeforeMount, Ref, ref, watch } from 'vue';
import {
  useAddLiquidityStore,
  useBurnLiquidityStore,
  useCreatePoolStore,
  useSwapStore,
  useTokensStore,
} from '@/store';
import { TokenIcon } from '@/components/UI/TokenIcon';

const dialogRef = inject<Ref<any>>('dialogRef');
const tokensStore = useTokensStore();

const tokenPreview = ref();
const display = ref(false);

const mode: Ref<'to' | 'from'> = ref(dialogRef?.value.data.mode);
const storeValue: Ref<'swap' | 'add' | 'burn' | 'create'> = ref(
  dialogRef?.value.data.store,
);
const token: Ref<string> = ref(dialogRef?.value.data.token);

const store =
  storeValue.value == 'swap'
    ? useSwapStore()
    : storeValue.value == 'add'
    ? useAddLiquidityStore()
    : storeValue.value == 'burn'
    ? useBurnLiquidityStore()
    : storeValue.value == 'create'
    ? useCreatePoolStore()
    : undefined;

const emits = defineEmits(['close', 'imported']);

watch(
  () => token.value,
  () => token.value && getToken(),
);

onBeforeMount(() => {
  token.value && getToken();
});

async function getToken() {
  let firstPart = token.value;
  if (token.value.indexOf('<') !== -1) {
    firstPart = token.value.substring(0, token.value.indexOf('<'));
  }
  if (firstPart.split('::').length !== 3) return;
  const tokenInfo = await tokensStore.getTokenInfo(token.value);
  tokenPreview.value = tokenInfo && {
    ...tokenInfo,
    address: firstPart.split('::')[0],
  };
}

async function importToken() {
  await tokensStore.registerToken(tokenPreview.value, { rewrite: false });
  if (tokenPreview.value && mode.value && store) {
    if (mode.value === 'to') store.toCurrency.token = token.value;
    if (mode.value === 'from') store.fromCurrency.token = token.value;
  }

  // old api for transmitting events before switching to the service
  emits('imported');
  emits('close');

  // after
  dialogRef.value.data.onImported = true;
  dialogRef.value.close({ isImportClicked: true });
}

function show() {
  display.value = token.value && tokenPreview.value && true;
}

function hide() {
  display.value = false;
}

function onClose() {
  dialogRef?.value.close({ isCloseClicked: true });
}

defineExpose({
  show,
  hide,
});
</script>
<style lang="scss" scoped>
.footer-buttons {
  width: 100%;
  display: flex;
  flex-direction: row;
  grid-gap: 16px;
}
.token-info {
  display: flex;
  align-items: center;
  padding: 18px;
  border-radius: 20px;
  background: #28253e;
  flex-direction: column;

  img,
  .img {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
  }

  &-content {
    display: flex;
    flex-direction: row;
    grid-gap: 8px;

    & > span:first-child {
      font-weight: 700;
      font-size: 17px;
      line-height: 24px;
      letter-spacing: 0.1px;
    }

    & > span:last-child {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: #d4c4ed;
      font-size: 17px;
      line-height: 24px;
      font-weight: 400;
    }
  }
  &-address {
    font-weight: 700;
    font-size: 13px;
    line-height: 24px;
    letter-spacing: 0.1px;
    text-align: center;
    word-wrap: break-word;
    width: 95%;
  }
}

.warning-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 76px 0px 100px;

  @media screen and (max-width: 767px) {
    margin: 32px 0px;
  }

  img {
    width: 64px;
    height: 64px;
    margin-bottom: 2rem;

    @media screen and (max-width: 767px) {
      width: 45px;
      height: 45px;
    }
  }
  &-content {
    display: flex;
    flex-direction: column;
    grid-gap: 8px;
    align-items: center;

    & > span:first-child {
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: 0.1px;
    }

    & > span:last-child {
      display: flex;
      align-items: center;
      text-align: center;
      font-size: 15px;
      line-height: 24px;
      font-weight: 400;
      color: #e5e4fa;
    }
  }
}
</style>
